import type { ComponentProps, FC, PropsWithChildren, ReactElement } from 'react';

import { memo, useMemo } from 'react';
import isEqual from 'react-fast-compare';

import { S } from './styles';
import type { CarouselTitle } from '~/components/shared/core/carousel/CarouselTitle';
import { useMediaQueryContext } from '~/utilities/context/dynamic/MediaQueryContext';

interface CarouselProps {
  id?: string;
  visibleOnDesktop?: boolean;
  visibleOnTablet?: boolean;
  visibleOnMobile?: boolean;
  title?: ReactElement<ComponentProps<typeof CarouselTitle>, typeof CarouselTitle>;
  className?: string;
}

const CarouselComponent: FC<CarouselProps & PropsWithChildren> = ({
  id = 'carousel',
  visibleOnTablet = false,
  visibleOnMobile = false,
  visibleOnDesktop = false,
  title,
  children,
  className,
}) => {
  const { breakpoint } = useMediaQueryContext();

  const isVisible = useMemo(
    () =>
      ({
        mobile: visibleOnMobile,
        tablet: visibleOnTablet,
        desktop: visibleOnDesktop,
      }[breakpoint ?? 'desktop']),
    [breakpoint, visibleOnDesktop, visibleOnMobile, visibleOnTablet]
  );

  return (
    <S.CarouselWrapper
      className={className}
      data-testid={id}
      data-visible={isVisible}
      aria-hidden={!isVisible}
      role="region"
      aria-roledescription="carousel"
      aria-labelledby={`${id}-title`}
    >
      {title}
      {children}
    </S.CarouselWrapper>
  );
};

export const Carousel = memo(CarouselComponent, isEqual);
